import { NumberArray } from '@wonderlandengine/api';
import { Option, Options, LeadInputValues, WebService } from '../services/web-service.js';
import {
	Configuration,
	WheelTypes,
	DoorGraphicTypes,
	LeatherTypes,
	PaintTypes,
	SeatBackStyles,
	SpareWheelOptions,
	AudioOptions,
	PaintColourWaysOptions,
	BespokePaintColours,
	WheelNutOptions,
	AccentOptions
} from '../data/configuration.js';
import { Utilities } from '../utilities/utilities.ts'
import { ModalContent } from '../components/ui/modal.tsx'

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

export type SupportedRegion = {
	id: string,
	name: string,
}

export type DealerDetails = {
	id: string,
	name?: string,
	subtitle?: string,
	address1?: string,
	address2?: string,
	address3?: string,
	address4?: string,
	postcode?: string,
	country?: string,
	telephone?: string,
	email?: string,
	lat?: string,
	lng?: string,
	company?: string,
}

/**
 * Cloud Data:
 */
export abstract class CloudData {

	//-----------------------------------------------------------------------------------------
	// Fields:
	//-----------------------------------------------------------------------------------------

	private static _transmissionOptions: Options;
	private static _paintworkColourOptions: Options;
	private static _paintworkColourWaysOptions: Options;
	private static _wheelStyleOptions: Options;
	private static _wheelNutOptions: Options;
	private static _leatherStyleOptions: Options;
	private static _seatBackStyleOptions: Options;
	private static _leatherPipingOptions: Options;
	private static _seatStitchingOptions: Options;
	private static _carpetColourOptions: Options;
	private static _dashboardOptions: Options;
	private static _steeringWheelOptions: Options;
	private static _hoodStyleOptions: Options;
	private static _wingBeadingOptions: Options;
	private static _seatPleatingOptions: Options;
	private static _headrestEmbroideryOptions: Options;
	private static _centreConsoleOptions: Options;
	private static _stoneGuardOptions: Options;
	private static _grilleMeshOptions: Options;
	private static _graphicsOptions: Options;
	private static _badgeOptions: Options;
	private static _accessoryOptions: Options;

	// the list of supported countries/regions, used to populate the country drop-down and to validate the auto-detected region.
	// n.b. we could also load this from the cloud, but Morgan may wish to only support certain countries, so we will hard-code the list for now.
	private static _supportedRegions: SupportedRegion[] = [
		{ id: 'gb', name: 'United Kingdom' },
		{ id: 'us', name: 'USA' },
		{ id: 'au', name: 'Australia' },
		{ id: 'at', name: 'Austria' },
		{ id: 'be', name: 'Belgium' },
		{ id: 'fr', name: 'France' },
		{ id: 'de', name: 'Germany' },
		{ id: 'it', name: 'Italy' },
		{ id: 'jp', name: 'Japan' },
		{ id: 'lu', name: 'Luxembourg' },
		{ id: 'nl', name: 'Netherlands' },
		{ id: 'no', name: 'Norway' },
		{ id: 'pl', name: 'Poland' },
		{ id: 'pt', name: 'Portugal' },
		{ id: 'es', name: 'Spain' },
		{ id: 'se', name: 'Sweden' },
		{ id: 'ch', name: 'Switzerland' }
	];
	
	//-----------------------------------------------------------------------------------------
	// Properties:
	//-----------------------------------------------------------------------------------------

	static get transmissionOptions() {
		return this._transmissionOptions;
	}

	static get paintworkColourOptions() {
		return this._paintworkColourOptions;
	}
	
	static get paintworkColourWaysOptions() {
		return this._paintworkColourWaysOptions;
	}

	static get wheelStyleOptions() {
		return this._wheelStyleOptions;
	}

	static get wheelNutOptions() {
		return this._wheelNutOptions;
	}

	static get leatherStyleOptions() {
		return this._leatherStyleOptions;
	}

	static get seatBackStyleOptions() {
		return this._seatBackStyleOptions;
	}

	static get leatherPipingOptions() {
		return this._leatherPipingOptions;
	}

	static get seatStitchingOptions() {
		return this._seatStitchingOptions;
	}

	static get carpetColourOptions() {
		return this._carpetColourOptions;
	}

	static get dashboardOptions() {
		return this._dashboardOptions;
	}

	static get steeringWheelOptions() {
		return this._steeringWheelOptions;
	}

	static get hoodStyleOptions() {
		return this._hoodStyleOptions;
	}

	static get wingBeadingOptions() {
		return this._wingBeadingOptions;
	}

	static get seatPleatingOptions() {
		return this._seatPleatingOptions;
	}

	static get headrestEmbroideryOptions() {
		return this._headrestEmbroideryOptions;
	}

	static get centreConsoleOptions() {
		return this._centreConsoleOptions;
	}

	static get stoneGuardOptions() {
		return this._stoneGuardOptions;
	}

	static get grilleMeshOptions() {
		return this._grilleMeshOptions;
	}

	static get graphicsOptions() {
		return this._graphicsOptions;
	}

	static get badgeOptions() {
		return this._badgeOptions;
	}

	static get accessoryOptions() {
		return this._accessoryOptions;
	}

	static get supportedRegions() {
		return this._supportedRegions;
	}

	//-----------------------------------------------------------------------------------------
	// Public Functions:
	//-----------------------------------------------------------------------------------------

	// an async function that loads options data from the cloud, populating the above fields and returning a promise when done.
	static async loadOptions(modelId: string, regionCode: string, languageCode: string): Promise<void> {
		await WebService.getOptions('TransmissionOptions', modelId, regionCode, languageCode).then(results => this._transmissionOptions = results);
		await WebService.getOptions('PaintworkColours', modelId, regionCode, languageCode).then(results => this._paintworkColourOptions = results);
		await WebService.getOptions('PaintworkColourWays', modelId, regionCode, languageCode).then(results => this._paintworkColourWaysOptions = results);
		await WebService.getOptions('WheelStyles', modelId, regionCode, languageCode).then(results => this._wheelStyleOptions = results);
		await WebService.getOptions('WheelNutOptions', modelId, regionCode, languageCode).then(results => this._wheelNutOptions = results);
		await WebService.getOptions('LeatherGrainStyles', modelId, regionCode, languageCode).then(results => this._leatherStyleOptions = results);
		await WebService.getOptions('SeatBackOptions', modelId, regionCode, languageCode).then(results => this._seatBackStyleOptions = results);
		await WebService.getOptions('LeatherPiping', modelId, regionCode, languageCode).then(results => this._leatherPipingOptions = results);
		await WebService.getOptions('SeatStitching', modelId, regionCode, languageCode).then(results => this._seatStitchingOptions = results);
		await WebService.getOptions('CarpetColours', modelId, regionCode, languageCode).then(results => this._carpetColourOptions = results);
		await WebService.getOptions('DashboardOptions', modelId, regionCode, languageCode).then(results => this._dashboardOptions = results);
		await WebService.getOptions('SteeringWheelCentreStyles', modelId, regionCode, languageCode).then(results => this._steeringWheelOptions = results);
		await WebService.getOptions('HoodStyles', modelId, regionCode, languageCode).then(results => this._hoodStyleOptions = results);
		await WebService.getOptions('WingBeading', modelId, regionCode, languageCode).then(results => this._wingBeadingOptions = results);
		await WebService.getOptions('SeatPleatingOptions', modelId, regionCode, languageCode).then(results => this._seatPleatingOptions = results);
		await WebService.getOptions('HeadrestEmbroidery', modelId, regionCode, languageCode).then(results => this._headrestEmbroideryOptions = results);
		await WebService.getOptions('CentreColumnOptions', modelId, regionCode, languageCode).then(results => this._centreConsoleOptions = results);
		await WebService.getOptions('StoneGuardStyles', modelId, regionCode, languageCode).then(results => this._stoneGuardOptions = results);
		await WebService.getOptions('GrilleMeshStyles', modelId, regionCode, languageCode).then(results => this._grilleMeshOptions = results);
		await WebService.getOptions('GraphicsOptions', modelId, regionCode, languageCode).then(results => this._graphicsOptions = results);
		await WebService.getOptions('Badges', modelId, regionCode, languageCode).then(results => this._badgeOptions = results);
		await WebService.getOptions('Options', modelId, regionCode, languageCode).then(results => this._accessoryOptions = results);

		// add tunnel side options to leather style options.
		await WebService.getOptions('LeatherOptions', modelId, regionCode, languageCode).then(results => this._leatherStyleOptions = { ...this._leatherStyleOptions, ...results });
		// n.b. the luggage rack, heated seat and audio options are appended to the accessory options.
		await WebService.getOptions('LuggageRackOptions', modelId, regionCode, languageCode).then(results => this._accessoryOptions = { ...this._accessoryOptions, ...results });
		await WebService.getOptions('HeatedSeatOptions', modelId, regionCode, languageCode).then(results => this._accessoryOptions = { ...this._accessoryOptions, ...results });
		await WebService.getOptions('AudioOptions', modelId, regionCode, languageCode).then(results => this._accessoryOptions = { ...this._accessoryOptions, ...results });
	}

	static isValidOption = (obj: object, id: string): boolean => {
		return Object.values(obj).includes(id);
	}

	// checks whether the given region code is one of the supported countries/regions.
	static isSupportedRegion(regionCode: string): boolean {
		return this._supportedRegions.some(region => region.id === regionCode);
	}

	// an async function that loads configuration data from the cloud, returning a promise when done.
	static async loadConfiguration(shortCode: string): Promise<void> {
		await WebService.loadConfiguration(shortCode).then(result => {

			// reference the relevant nodes.
			const options = result.options;
			const model = options.model;
			const colourways = options.paintwork.colourways;
			const interior = options.interior_textiles;
			const leather = interior.leather;
			const seats = interior.seats;
			const cockpit = options.cockpit;
			const finalFinishes = options.final_finishes;
			const exterior = options.exterior_textiles;
			const cowl = finalFinishes.cowl;
			const callSigns = finalFinishes?.callsigns;

			// ensure the loaded configuration is for this car model.
			if (model.type.id !== Configuration.modelId) {
				throw new Error('ShortCode provided is for a different model');
			}

			// ensure the required loaded options are valid.

			if (!this._transmissionOptions[model.gearbox.id]) {
				throw new Error('Invalid gearbox ID loaded');
			}
			if (!this._paintworkColourOptions[colourways.paint_body.id]) {
				throw new Error('Invalid body paint ID loaded');
			}
			if (!this._wheelStyleOptions[options.wheels.style.id]) {
				throw new Error('Invalid wheel style ID loaded');
			}
			if (!this._seatBackStyleOptions[seats.seat_back.id]) {
				throw new Error('Invalid seat back style ID loaded');
			}
			if (!this._leatherPipingOptions[interior.piping.id]) {
				throw new Error('Invalid leather piping style ID loaded');
			}
			if (!this._seatStitchingOptions[interior.stitching.id]) {
				throw new Error('Invalid seat stitching style ID loaded');
			}
			if (!this._carpetColourOptions[interior.carpet.id]) {
				throw new Error('Invalid carpet style ID loaded');
			}
			if (!this._dashboardOptions[cockpit.dashboard_face.id]) {
				throw new Error('Invalid dashboard style ID loaded');
			}
			if (!this._steeringWheelOptions[cockpit.steering_wheel_centre.id]) {
				throw new Error('Invalid steering wheel ID loaded');
			}
			if (!this._hoodStyleOptions[exterior.hood.id]) {
				throw new Error('Invalid hood style ID loaded');
			}
			if (!this._wingBeadingOptions[exterior.wing_beading.id]) {
				throw new Error('Invalid wing beading style ID loaded');
			}
			if (!this._seatPleatingOptions[interior.seats.pleating.id]) {
				throw new Error('Invalid seat pleating style ID loaded');
			}
			if (!this._headrestEmbroideryOptions[interior.seats.headrest_embroidery.id]) {
				throw new Error('Invalid headrest embroidery style ID loaded');
			}
			if (!this._centreConsoleOptions[cockpit.center_column.id]) {
				throw new Error('Invalid centre console ID loaded');
			}
			if (!this._stoneGuardOptions[cowl[0]?.id]) {
				throw new Error('Invalid guard finish ID loaded');
			}
			if (!this._grilleMeshOptions[cowl[1]?.id]) {
				throw new Error('Invalid mesh finish ID loaded');
			}

			//
			// update the configuration.
			//

			// transmission -------------------------------------------------------------

			Configuration.transmission = model.gearbox.id;
			
			// paint --------------------------------------------------------------------

			Configuration.bodyPaintColour = colourways.paint_body.id;

			// the bespoke colours are cleared first, so that a standard colour in the loaded configuration drops any colour left over
			// from earlier in the session rather than leaving it applied over the loaded paint. a missing or malformed hex is not
			// fatal: it logs and leaves the colour null, so the option keeps its description and price and the car keeps its paint -
			// rejecting the load would make every configuration saved before the field existed unloadable.
			Configuration.bespokeBodyPaintColour = this.readBespokePaintColour(Configuration.bodyPaintColour, colourways.paint_body?.option_hex);

			// set the wing colour to a separate colour if provided, or to the provided body colour.
			if (colourways.paint_wings) {
				Configuration.wingPaintColour = colourways.paint_wings.id;
				Configuration.bespokeWingPaintColour = this.readBespokePaintColour(Configuration.wingPaintColour, colourways.paint_wings.option_hex);
			}
			else {
				Configuration.wingPaintColour = Configuration.bodyPaintColour;
				Configuration.bespokeWingPaintColour = Configuration.bespokeBodyPaintColour;
			}
			
			// wheels --------------------------------------------------------------------

			Configuration.wheelStyle = options.wheels.style.id;
			if (options.wheels?.wheel_nuts?.id) {
				Configuration.wheelNutOption = options.wheels.wheel_nuts?.id;
			}
			Configuration.spareWheelOption = options.wheels?.spare_wheel ? SpareWheelOptions.Spare : SpareWheelOptions.None;


			// interior ------------------------------------------------------------------

			// materials.

			// a leather that has been discontinued, or that the back office no longer lists, falls back to the default rather than the whole
			// configuration being rejected. n.b. discontinued IDs are checked explicitly, as the back office may still list them.
			if (this._leatherStyleOptions[leather.style.id] && !Configuration.discontinuedLeatherStyleIds.includes(leather.style.id)) {
				Configuration.leatherStyle = leather.style.id;
			} else {
				console.error('Invalid or discontinued leather style ID loaded, using default: ', leather.style.id);
				Configuration.leatherStyle = Configuration.defaultLeatherStyleId;
			}

			Configuration.tunnelSidesStyle = leather.trim.id;

			Configuration.seatPleatingOption = seats?.pleating?.id;

			if (this.isValidOption(SeatBackStyles, seats?.seat_back?.id)) {
				Configuration.seatBackStyle = seats?.seat_back?.id;
			} else {
				Configuration.seatBackStyle = SeatBackStyles.TwoToneTextile;
			}

			// a two-tone leather dictates its own seat back and pleating styles (as in the leather view), so re-apply them here - the
			// default leather above is two-tone, but the configuration it replaced may have been saved with a single-colour one.
			if (this.getLeatherTypesForId(Configuration.leatherStyle).leatherType === LeatherTypes.TwoTone) {
				Configuration.seatBackStyle = SeatBackStyles.TwoToneTextile;
				Configuration.seatPleatingOption = Configuration.seatPleatingOptionForTwoToneLeather;
			}

			Configuration.headrestEmbroideryOption = interior.seats.headrest_embroidery.id;

			Configuration.leatherPipingOption = interior.piping.id;
			Configuration.seatStitchingOption = interior.stitching.id;

			Configuration.carpetColour = interior.carpet.id;

			// cockpit.

			Configuration.dashboardOption = options.cockpit.dashboard_face.id;
			Configuration.steeringWheelOption = options.cockpit.steering_wheel_centre.id;

			Configuration.centreConsoleOption = options.cockpit.center_column.id;
			
			// exterior ------------------------------------------------------------------

			// textiles.

			Configuration.hoodStyleOption = exterior.hood.id;
			Configuration.wingBeadingOption = exterior.wing_beading.id;

			// cowl.

			Configuration.stoneGuardOption = cowl?.[0]?.id;
			Configuration.grilleMeshOption = cowl?.[1]?.id;

			// call signs.

			Configuration.callSignsFlagOption = callSigns?.[0]?.id || 'NONE';
			Configuration.pilotValue = callSigns?.[0]?.pilot || '';

			// old configurator stores copilot as 'co-pilot' in the JSON, I would like the new configurator to use 'copilot' instead so we need to check for both.
			Configuration.copilotValue = callSigns?.[0]?.['copilot'] ?? callSigns?.[0]?.['co-pilot'] ?? '';

			// graphics.

			if (options.final_finishes.graphics?.length) {
				Configuration.doorGraphicOption = options.final_finishes.graphics[0]?.id;
			}

			// final finishes.
			
			Configuration.splitterOption = options.final_finishes.splitter.id;
			if (options.final_finishes.accents?.length) {
				Configuration.accentPackOption = options.final_finishes?.accents?.[0]?.id;
				Configuration.accentOption = 'NONE';
			} else {
				Configuration.accentPackOption = 'NONE';
				Configuration.accentOption = options.final_finishes?.badges?.id;
			}

			if (options.final_finishes.paint_protection_film?.id) {
				Configuration.paintProtectionFilmOption = options.final_finishes.paint_protection_film.id;
			}

			// accessories ------------------------------------------------------------------

			// storage.

			const sidescreenStorage = options.accessories?.storage_bags?.find((item) => item.id === Configuration.sidescreenStorageOption);
			if (sidescreenStorage) {
				Configuration.addAccessoryOption(Configuration.accessoryStorageOptions, sidescreenStorage.id);
			}

			const tunnelStorage = options.cockpit.convenience.find((item) => item.id === Configuration.tunnelStorageOption);
			if (tunnelStorage) {
				Configuration.addAccessoryOption(Configuration.accessoryStorageOptions, tunnelStorage.id);
			}

			// covers.

			options.accessories?.covers?.forEach((cover) => {
				Configuration.addAccessoryOption(Configuration.accessoryCoversOptions, cover.id);
			});

			// equipment.

			options.accessories?.equipment?.forEach((equipmentItem) => {
				Configuration.addAccessoryOption(Configuration.accessoryEquipmentOptions, equipmentItem.id);
			});

			if (seats?.heated_seats?.id) {
				Configuration.addAccessoryOption(Configuration.accessoryEquipmentOptions, seats.heated_seats.id);
			}

			if (seats?.comfort_seats?.id) {
				Configuration.addAccessoryOption(Configuration.accessoryEquipmentOptions, seats.comfort_seats.id);
			}

			const airCon = options.cockpit.convenience.find((item) => item.id === Configuration.airConOption);
			if (airCon) {
				Configuration.addAccessoryOption(Configuration.accessoryEquipmentOptions, airCon.id);
			}

			const connectivityPack = options.cockpit.convenience.find((item) => item.id === Configuration.connectivityPackOption);
			if (connectivityPack) {
				Configuration.addAccessoryOption(Configuration.accessoryEquipmentOptions, connectivityPack.id);
			}

			const cupHolder = options.cockpit.convenience.find((item) => item.id === Configuration.cupHolderOption);
			if (cupHolder) {
				Configuration.addAccessoryOption(Configuration.accessoryEquipmentOptions, cupHolder.id);
			}
			
			// services.

			options.accessories?.services?.forEach((servicesItem) => {
				Configuration.addAccessoryOption(Configuration.accessoryServicesOptions, servicesItem.id);
			});

			// aftermarket.

			options.accessories?.aftermarket?.forEach((aftermarketItem) => {
				Configuration.addAccessoryOption(Configuration.accessoryAftermarketOptions, aftermarketItem.id);
			});

			// audio.
			
			const audio = options.cockpit.convenience.find((item) => item.id === AudioOptions.Bluetooth || item.id === AudioOptions.Sennheiser);
			if (audio) {
				Configuration.audioOption = audio.id;
			}
		});
	}
	
	// configure accessory options.
	static handleSavedAccessoryOptions(options: Set<string>) {

		// return if invalid.
		if (!(options instanceof Set)) {
			console.error('Invalid options:', options);
			return [];
		}

		// map over the options array and return a new array of objects.
		return Array.from(options).map((option) => {

			const price = this.getPrice(option,
				this.accessoryOptions,
				{
					ref: false
				});
			return {
				id: option,
				name: this.getName(option, this._accessoryOptions),
				price: Utilities.asCurrency(price, Configuration.regionCode),
			};
		});
	}

	// configure summary item.
	static configureSummaryItem(options: Options, optionCode: string, rowName?: string) {

		// return null if no option code.
		if (!optionCode || optionCode === 'NONE' || optionCode === 'N/A') return null;

		const price = this.getPrice(optionCode,
			options,
			{
				ref: false
			});


		// return a configured object for each summary item.
		return {
			name: rowName ? rowName : '',
			option_code: optionCode,
			value: this.getName(optionCode, options),
			price: Utilities.asCurrency(price, Configuration.regionCode)
		};
	}

	// generate the summary array.
	static async generateSummary() {

		const configureSummaryItem = this.configureSummaryItem.bind(this);

		// gearbox.

		const gearbox = {
			group: 'Gearbox',
			items: [
				configureSummaryItem(this.transmissionOptions, Configuration.transmission, 'Gearbox')
			].filter(Boolean)
		}

		// paintwork.

		const paintToneId = Configuration.isTwoTonePaint()
			? PaintColourWaysOptions.TwoTone
			: PaintColourWaysOptions.SingleColour;

		const wingPaint = Configuration.isTwoTonePaint() ?
			configureSummaryItem(this.paintworkColourOptions, Configuration.wingPaintColour, 'Wings Paint') : 
			{
				name: 'Wings Paint',
				option_code: Configuration.wingPaintColour,
				value: this.getName(Configuration.wingPaintColour, this.paintworkColourOptions),
				price: Utilities.asCurrency(0, Configuration.regionCode)
			}

		const paintwork = {
			group: 'Paintwork',
			items: [
				configureSummaryItem(this.paintworkColourWaysOptions, paintToneId, 'Colourways'),
				configureSummaryItem(this.paintworkColourOptions, Configuration.bodyPaintColour, 'Paint'),
				wingPaint
			].filter(Boolean)
		}

		// wheels.

		const wheelId = Configuration.spareWheelOption === SpareWheelOptions.Spare ? CloudData.getSpareWheelId(Configuration.wheelStyle) : Configuration.wheelStyle;

		const wheels = {
			group: 'Wheels',
			items: [
				configureSummaryItem(this.wheelStyleOptions, wheelId, 'Wheel Style'),
				configureSummaryItem(this.wheelNutOptions, Configuration.wheelNutOption, 'Wheel Nuts')
			].filter(Boolean)
		}

		// interior textiles.

		const interiorTextiles = {
			group: 'Interior Textiles',
			items: [
				configureSummaryItem(this.leatherStyleOptions, Configuration.leatherStyle, 'Leather'),
				configureSummaryItem(this.seatPleatingOptions, Configuration.seatPleatingOption, 'Pleating'),
				configureSummaryItem(this.seatBackStyleOptions, Configuration.seatBackStyle, 'Seat Backs'),
				configureSummaryItem(this.headrestEmbroideryOptions, Configuration.headrestEmbroideryOption, 'Headrest Embroidery'),
				configureSummaryItem(this.seatStitchingOptions, Configuration.seatStitchingOption, 'Stitching'),
				configureSummaryItem(this.carpetColourOptions, Configuration.carpetColour, 'Carpet'),
				configureSummaryItem(this.leatherStyleOptions, Configuration.tunnelSidesStyle, 'Tunnel Sides')
			].filter(Boolean)
		}

		// cockpit.

		const cockpit = {
			group: 'Cockpit',
			items: [
				configureSummaryItem(this.dashboardOptions, Configuration.dashboardOption, 'Dashboard Face'),
				configureSummaryItem(this.centreConsoleOptions, Configuration.centreConsoleOption, 'Centre Console'),
				configureSummaryItem(this.steeringWheelOptions, Configuration.steeringWheelOption, 'Steering Wheel')
			].filter(Boolean)
		}

		// exterior textiles.

		const exteriorTextiles = {
			group: 'Exterior Textiles',
			items: [
				configureSummaryItem(this.hoodStyleOptions, Configuration.hoodStyleOption, 'Hood'),
				configureSummaryItem(this.wingBeadingOptions, Configuration.wingBeadingOption, 'Wing Beading'),
				configureSummaryItem(this.stoneGuardOptions, Configuration.stoneGuardOption, 'Stone Guard'),
				configureSummaryItem(this.grilleMeshOptions, Configuration.grilleMeshOption, 'Grille Mesh'),
				configureSummaryItem(this.graphicsOptions, Configuration.doorGraphicOption, 'Door Graphics'),
				configureSummaryItem(this.accessoryOptions, Configuration.splitterOption, 'Splitter/Diffuser'),
				configureSummaryItem(this.badgeOptions, Configuration.accentOption, 'Badges'),
				configureSummaryItem(this.accessoryOptions, Configuration.accentPackOption, 'Accent Pack'),
				configureSummaryItem(this.accessoryOptions, Configuration.paintProtectionFilmOption, 'Paint Protection Film')
			].filter(Boolean)
		}

		// accessories.

		const storage = Array.from(Configuration.accessoryStorageOptions).map(option => configureSummaryItem(this.accessoryOptions, option));
		const covers = Array.from(Configuration.accessoryCoversOptions).map(option => configureSummaryItem(this.accessoryOptions, option));
		const equipment = Array.from(Configuration.accessoryEquipmentOptions).map(option => configureSummaryItem(this.accessoryOptions, option));
		const services = Array.from(Configuration.accessoryServicesOptions).map(option => configureSummaryItem(this.accessoryOptions, option));
		const aftermarket = Array.from(Configuration.accessoryAftermarketOptions).map(option => configureSummaryItem(this.accessoryOptions, option));

		const accessories = {
			group: 'Accessories',
			items: [
				...storage,
				...covers,
				...equipment,
				...services,
				...aftermarket,
				configureSummaryItem(this.accessoryOptions, Configuration.audioOption, 'Audio')
			].filter(Boolean)
		}

		return [
			gearbox,
			paintwork,
			wheels,
			interiorTextiles,
			cockpit,
			exteriorTextiles,
			accessories
		];
	}

	// generate options to be used when saving and generating a PDF.
	static async generateOptions() {
		
		// handle price universally.
		const handlePrice = (id: string, idOptions: Options) => {

			const price = this.getPrice(id,
				idOptions,
				{
					ref: false
				});

			return price;
		}


		// model ------------------------------------------------------------------

		const model = {
			type: {
				id: Configuration.modelId,
				name: 'Plus Four',
				price: -1.00
			},
			gearbox: {
				id: Configuration.transmission,
				name: this.getName(Configuration.transmission, this._transmissionOptions),
				price: handlePrice(Configuration.transmission, this._transmissionOptions),
			}
		}

		// paintwork ------------------------------------------------------------------

		const paintToneId = Configuration.isTwoTonePaint()
			? PaintColourWaysOptions.TwoTone
			: PaintColourWaysOptions.SingleColour;

		const paintwork: { colourways: { two_tone: any, paint_body: any, paint_wings?:any }} = {
			colourways: {
				two_tone: {
					id: paintToneId,
					name: Configuration.isTwoTonePaint() ? 'Two Tone' : 'Single Colour',
					price: handlePrice(
						paintToneId,
						CloudData.paintworkColourWaysOptions),
				},
				paint_body: {
					id: Configuration.bodyPaintColour,
					name: this.getName(Configuration.bodyPaintColour, this._paintworkColourOptions),
					lacquer_matt: false,
					price: handlePrice(Configuration.bodyPaintColour, this._paintworkColourOptions),
					laquer_price: 0.00,

					// a bespoke option id says which finish was chosen but not which colour, so the colour goes with it. the alpha is
					// forced off, as a bespoke colour is always opaque and the field is '#rrggbb'. omitted for every standard paint.
					...(Configuration.isBespokePaintColour(Configuration.bodyPaintColour) && Configuration.bespokeBodyPaintColour
						? { option_hex: Utilities.rgbaToHex(Configuration.bespokeBodyPaintColour, true) }
						: {})
				},
			}
		};

		if (paintToneId === PaintColourWaysOptions.TwoTone) {
			paintwork.colourways.paint_wings = {
				id: Configuration.wingPaintColour,
				name: this.getName(Configuration.wingPaintColour, this._paintworkColourOptions),
				lacquer_matt: false,

				// as for the body: the same bespoke option ids serve both regions, so without the colour beside it the id would not
				// say which of the two was chosen here.
				...(Configuration.isBespokePaintColour(Configuration.wingPaintColour) && Configuration.bespokeWingPaintColour
					? { option_hex: Utilities.rgbaToHex(Configuration.bespokeWingPaintColour, true) }
					: {}),
				price: handlePrice(Configuration.wingPaintColour, this._paintworkColourOptions),
				laquer_price: 0.00
			}
		}


		// wheels ------------------------------------------------------------------

		const wheels: { style: any, spare_wheel?: any, wheel_nuts?: any } = {

			// wheel style.
			style: {
				id: Configuration.wheelStyle,
				name: this.getName(Configuration.wheelStyle, this._wheelStyleOptions),
				price: handlePrice(Configuration.wheelStyle, this._wheelStyleOptions),
			},

		};

		// spare wheel.

		if (Configuration.spareWheelOption !== SpareWheelOptions.None) {
			const spareWheelId = CloudData.getSpareWheelId(Configuration.wheelStyle);


			wheels.spare_wheel = {
				id: spareWheelId,
				name: this.getName(spareWheelId, this._wheelStyleOptions),
				price: handlePrice(spareWheelId, this._wheelStyleOptions),
			}
		}

		// wheel nut.

		if (Configuration.wheelNutOption !== WheelNutOptions.None) {
			wheels.wheel_nuts = {
				id: Configuration.wheelNutOption,
				name: this.getName(Configuration.wheelNutOption, this._wheelNutOptions),
				price: handlePrice(Configuration.wheelNutOption, this._wheelNutOptions),
			}
		}


		// interior textiles ------------------------------------------------------------------

		let seatBackId = Configuration.seatBackStyle;
		if (seatBackId === SeatBackStyles.TwoToneTextile) {
			seatBackId = 'SB001';
		}

		const interiorTextiles: { leather: any, seats: any, piping: any, stitching: any, carpet: any } = {

			// leather.
			leather: {
				style: {
					id: Configuration.leatherStyle,
					name: this.getName(Configuration.leatherStyle, this._leatherStyleOptions),
					price: handlePrice(Configuration.leatherStyle, this._leatherStyleOptions),
				},
				trim: {
					id: Configuration.tunnelSidesStyle,
					name: this.getName(Configuration.tunnelSidesStyle, this._leatherStyleOptions),
					price: handlePrice(Configuration.tunnelSidesStyle, this._leatherStyleOptions),
				}
			},

			// seats.
			seats: {
				pleating: {
					id: Configuration.seatPleatingOption,
					name: this.getName(Configuration.seatPleatingOption, this._seatPleatingOptions),
					price: handlePrice(Configuration.seatPleatingOption, this._seatPleatingOptions),
				},
				seat_back: {
					id: seatBackId,
					name: this.getName(Configuration.seatBackStyle, this._seatBackStyleOptions),
					price: handlePrice(Configuration.seatBackStyle, this._seatBackStyleOptions),
				},
				headrest_embroidery: {
					id: Configuration.headrestEmbroideryOption,
					name: this.getName(Configuration.headrestEmbroideryOption, this._headrestEmbroideryOptions),
					price: handlePrice(Configuration.headrestEmbroideryOption, this._headrestEmbroideryOptions),
				}
			},

			// piping.
			piping: {
				id: Configuration.leatherPipingOption,
				name: this.getName(Configuration.leatherPipingOption, this._leatherPipingOptions),
				price: handlePrice(Configuration.leatherPipingOption, this._leatherPipingOptions),
			},

			// stitching.
			stitching: {
				id: Configuration.seatStitchingOption,
				name: this.getName(Configuration.seatStitchingOption, this._seatStitchingOptions),
				price: handlePrice(Configuration.seatStitchingOption, this._seatStitchingOptions),
			},

			// carpet.
			carpet: {
				id: Configuration.carpetColour,
				name: this.getName(Configuration.carpetColour, this._carpetColourOptions),
				price: handlePrice(Configuration.carpetColour, this._carpetColourOptions),
			}
		};
		
		// heated seats.
		if (Configuration.accessoryEquipmentOptions.has(Configuration.heatedSeatsOption)) {
			interiorTextiles.seats.heated_seats = {
				id: Configuration.heatedSeatsOption,
				name: this.getName(Configuration.heatedSeatsOption, this._accessoryOptions),
				price: handlePrice(Configuration.heatedSeatsOption, this._accessoryOptions),
			}
		}

		// comfort plus.
		if (Configuration.accessoryEquipmentOptions.has(Configuration.comfortPlusOption)) {
			interiorTextiles.seats.comfort_seats = {
				id: Configuration.comfortPlusOption,
				name: this.getName(Configuration.heatedSeatsOption, this._accessoryOptions),
				price: handlePrice(Configuration.heatedSeatsOption, this._accessoryOptions),
			}
		}


		// cockpit ------------------------------------------------------------------
		
		const cockpit: { dashboard_face: any, center_column: any, steering_wheel_centre: any, leather_details: any, convenience?: any } = {

			// dashboard face.
			dashboard_face: {
				id: Configuration.dashboardOption,
				name: this.getName(Configuration.dashboardOption, this.dashboardOptions),
				price: handlePrice(Configuration.dashboardOption, this.dashboardOptions),
			},

			// center column.
			center_column: {
				id: Configuration.centreConsoleOption,
				name: this.getName(Configuration.centreConsoleOption, this.centreConsoleOptions),
				price: handlePrice(Configuration.centreConsoleOption, this._centreConsoleOptions),
				lacquer_matt: true, // all wood options are now Matte.
				lacquer_price: 0.00 // not sure if we stil need to output this option, as wood prices are now inclusive of lacquer.
			},

			// steering wheel.
			steering_wheel_centre: {
				id: Configuration.steeringWheelOption,
				name: this.getName(Configuration.steeringWheelOption, this._steeringWheelOptions),
				price: handlePrice(Configuration.steeringWheelOption, this._steeringWheelOptions),
			},

			// leather details.
			leather_details: [{
				id: 'LT001',
				name: 'Black',
				price: '\u00a30.00'
			}]
		};

		// need to get out convenience from accessories.
		cockpit.convenience = [];

		// cupholder.
		if (Configuration.accessoryEquipmentOptions.has(Configuration.cupHolderOption)) {
			cockpit.convenience.push({
				id: Configuration.cupHolderOption,
				name: this.getName(Configuration.cupHolderOption, this._accessoryOptions),
				price: handlePrice(Configuration.cupHolderOption, this._accessoryOptions),
			});
		}

		// air con.
		if (Configuration.accessoryEquipmentOptions.has(Configuration.airConOption)) {
			cockpit.convenience.push({
				id: Configuration.airConOption,
				name: this.getName(Configuration.airConOption, this._accessoryOptions),
				price: handlePrice(Configuration.airConOption, this._accessoryOptions),
			});
		}

		// audio.
		cockpit.convenience.push({
			id: Configuration.audioOption,
			name: this.getName(Configuration.audioOption, this._accessoryOptions),
			price: handlePrice(Configuration.audioOption, this._accessoryOptions),
		});

		// tunnel storage.
		if (Configuration.accessoryStorageOptions.has(Configuration.tunnelStorageOption)) {
			cockpit.convenience.push({
				id: Configuration.tunnelStorageOption,
				name: this.getName(Configuration.tunnelStorageOption, this._accessoryOptions),
				price: handlePrice(Configuration.tunnelStorageOption, this._accessoryOptions),
			});
		}


		// exterior textiles ------------------------------------------------------------------

		const exteriorTextiles = {

			// hood.
			hood: {
				id: Configuration.hoodStyleOption,
				name: this.getName(Configuration.hoodStyleOption, this._hoodStyleOptions),
				price: handlePrice(Configuration.hoodStyleOption, this._hoodStyleOptions),
			},

			// wing beading.
			wing_beading: {
				id: Configuration.wingBeadingOption,
				name: this.getName(Configuration.wingBeadingOption, this._wingBeadingOptions),
				price: handlePrice(Configuration.wingBeadingOption, this._wingBeadingOptions),
			}
		};


		// final finishes ------------------------------------------------------------------

		let badgeId = Configuration.accentOption;
		if (badgeId === 'NONE') {
			badgeId = AccentOptions.NoBadge;
		}

		const finalFinishes: { cowl: any, splitter: any, badges: any, paint_protection_film?: any, accents?: any, graphics?: any, callsigns?: any } = {

			// cowl.
			cowl: [
				{
					id: Configuration.stoneGuardOption,
					name: this.getName(Configuration.stoneGuardOption, this._stoneGuardOptions),
					price: handlePrice(Configuration.stoneGuardOption, this._stoneGuardOptions),
				},
				{
					id: Configuration.grilleMeshOption,
					name: this.getName(Configuration.grilleMeshOption, this._grilleMeshOptions),
					price: handlePrice(Configuration.grilleMeshOption, this._grilleMeshOptions),
				}
			],

			// splitter.
			splitter: {
				id: Configuration.splitterOption,
				name: this.getName(Configuration.splitterOption, this.accessoryOptions),
				price: handlePrice(Configuration.splitterOption, this.accessoryOptions),
			},

			// badges.
			badges: {
				id: badgeId,
				name: this.getName(Configuration.accentOption, this.badgeOptions),
				price: handlePrice(Configuration.accentOption, this.badgeOptions),
			}
		};

		// accent packs.

		if (Configuration.accentPackOption !== 'NONE') {
			finalFinishes.badges = {
				id: Configuration.accentPackOption,
				name: this.getName(Configuration.accentPackOption, this.accessoryOptions),
				price: handlePrice(Configuration.accentPackOption, this.accessoryOptions)
			};
		}

		// paint protection film.

		if (Configuration.paintProtectionFilmOption !== 'NONE') {
			finalFinishes.paint_protection_film = {
				id: Configuration.paintProtectionFilmOption,
				name: this.getName(Configuration.paintProtectionFilmOption, this.accessoryOptions),
				price: handlePrice(Configuration.paintProtectionFilmOption, this.accessoryOptions),
			};
		}
		
		// graphics.

		if (Configuration.doorGraphicOption !== 'NONE') {

			// door graphic.
			finalFinishes.graphics = [...finalFinishes.graphics || [], {
				id: Configuration.doorGraphicOption,
				name: this.getName(Configuration.doorGraphicOption, this._graphicsOptions),
				price: handlePrice(Configuration.doorGraphicOption, this._graphicsOptions),
			}];
		}
		
		// call signs.

		if (Configuration.callSignsFlagOption !== 'NONE') {
			finalFinishes.callsigns = [
				{
					id: Configuration.callSignsFlagOption,
					name: this.getName(Configuration.callSignsFlagOption, this._graphicsOptions),
					price: handlePrice(Configuration.callSignsFlagOption, this._badgeOptions),
					pilot: Configuration.pilotValue,
					'co-pilot': Configuration.copilotValue,
					'custom-flag': '',
				}
			];
		}
		
		// accessories ------------------------------------------------------------------

		const accessories: { storage_bags?: any, covers?: any, equipment?: any, services?: any, aftermarket?: any } = {};

		// storage.
		if (Configuration.accessoryStorageOptions.size > 0) {
			accessories.storage_bags = this.handleSavedAccessoryOptions(Configuration.accessoryStorageOptions);
		}

		// covers.
		if (Configuration.accessoryCoversOptions.size > 0) {
			accessories.covers = this.handleSavedAccessoryOptions(Configuration.accessoryCoversOptions);
		}

		// equipment.
		if (Configuration.accessoryEquipmentOptions.size > 0) {
			accessories.equipment = this.handleSavedAccessoryOptions(Configuration.accessoryEquipmentOptions);
		}

		// services.
		if (Configuration.accessoryServicesOptions.size > 0) {
			accessories.services = this.handleSavedAccessoryOptions(Configuration.accessoryServicesOptions);
		}

		// aftermarket.
		if (Configuration.accessoryAftermarketOptions.size > 0) {
			accessories.aftermarket = this.handleSavedAccessoryOptions(Configuration.accessoryAftermarketOptions);
		}

		const options = {
			model,
			paintwork,
			wheels,
			interior_textiles: interiorTextiles,
			cockpit,
			exterior_textiles: exteriorTextiles,
			final_finishes: finalFinishes,
			accessories
		}

		return options;
	}

	static async sendToDealer(inputValues: LeadInputValues, shortCode: string, dealerInfo: DealerDetails | null, leadType: string, value: number, hasUserConsent: boolean, hasMarketingOptin: boolean) {

		// call the web service.
		const sendToDealer = await WebService.sendToDealer(inputValues, shortCode, dealerInfo?.id || '', leadType, value, hasUserConsent, hasMarketingOptin);
		return sendToDealer;
	}

	// save the configuration.
	static async saveConfiguration() {

		// generate the summary and options.
		const summary = await this.generateSummary();
		const options = await this.generateOptions();

		const saveConfigObj = {
			options,
			summary,
		}

		// call the web service.
		return await WebService.saveConfiguration(Configuration.regionCode, saveConfigObj);
	}

	static async generatePdf(shortCode: string, images: Blob[], email: string, firstName: string, lastName: string, sendEmailTemplate?: string, dealerName?: string, dealerCompanyName?: string, dealerEmail?: string) {

		// generate options.
		const options = await this.generateOptions();
		const summary = await this.generateSummary();

		// setup in the way the server expects.
		const pdfOptions = {
			'options': options,
			'summary': summary
		}

		// save images.
		const res = await WebService.saveImages(images);

		if (!res) return null;

		// convert object to array.
		const imageUrls = Object.values(res) as unknown as string[];

		// call the web service.
		return await WebService.generatePdf(Configuration.regionCode, Configuration.languageCode || 'en', shortCode, pdfOptions, imageUrls, email, firstName, lastName, sendEmailTemplate, dealerName, dealerCompanyName, dealerEmail);
	}

	static getPrice(id: string, options: Options, isInvalidOption: {ref: boolean}): number {

		// handle the special ID of 'NONE' and 'N/A', which denotes a valid option with no corresponding entry in the options table.
		if (id === 'NONE' || id ==='N/A') {
			return 0;
		}

		// get the price from the relevant option, returning zero if not found or not valid.
		let price = 0;
		const option = this.getOption(id, options);
		if (option) {
			price = option.price;

			// a value of "-1" means the option is not valid in the current region, so return the price as zero and set the given flag.
			if (price === -1) {
				isInvalidOption.ref = true;
				price = 0;
			}
		}
		return price;
	}

	static getAccessoryPrices(accessoryOptions: Set<string>, isInvalidOption: { ref: boolean }): number {
		let price = 0;
		accessoryOptions.forEach((item) => {
			price += this.getPrice(item, this.accessoryOptions, isInvalidOption);
		});
		return price;
	}

	static getName(id: string, options: Options): string {

		// handle the special ID of 'NONE', which denotes a valid option with no corresponding entry in the options table.
		if (id === 'NONE') {
			return 'None';
		}

		// handle the special ID of 'N/A', which denotes a valid option with no corresponding entry in the options table.
		if (id === 'N/A') {
			return 'N/A';
		}

		// look up the ID in the options, and return the relevant option name.
		const option = this.getOption(id, options);
		if (option) {
			return option.name;
		}

		// or, log an error if not found, and return an empty string.
		console.error(`ID: ${id} cannot be found in the options data`);
		return '';
	};

	static getDescription(id: string, options: Options): string {

		// handle the special ID of 'NONE' and 'N/A', which denotes a valid option with no corresponding entry in the options table.
		if (id === 'NONE' || id === 'N/A') {
			return '';
		}

		// look up the ID in the options, and return the relevant option description.
		const option = this.getOption(id, options);
		if (option) {
			return option.description;
		}

		// or, log an error if not found, and return an empty string.
		console.error(`ID: ${id} cannot be found in the options data`);
		return '';
	};

	static getModalContentItem(id: string, options: Options) {

		// get description and name.
		const description = this.getDescription(id, options);
		const name = this.getName(id, options);

		// return nothing if there is no description
		if (!description) return null;

		const content = {
			title: name,
			body: description
		}

		// return the content object to be used by a modal.
		return content;
	}


	static getModalContent(id: string, options: Options) {

		// get content for modal and return it as an array so it can be correctly processed by Modal.tsx.
		const item = this.getModalContentItem(id, options);

		if (!item) return [];

		return [item];
	}

	static getAccessoryModalContent(selectedOptions: Set<string>) {
		const content: ModalContent[] = [];

		// handle multiple accessories and return as an array of objects.
		selectedOptions.forEach((item) => {
			const itemDescription = this.getModalContentItem(item, this.accessoryOptions);
			if (itemDescription) {
				content.push(itemDescription);
			}
		});

		return content;
	}

	static getAccessoryDescriptions(selectedOptions: Set<string>): string[] {

		// create array of descriptions as multiple accessories from the same section can be selected.
		const descriptions: string[] = [];

		selectedOptions.forEach((item) => {
			const itemDescription = this.getDescription(item, this.accessoryOptions);
			if (itemDescription) {
				descriptions.push(itemDescription);
			}
		});

		return descriptions;
	}

	static getOption(id: string, options: Options): Option | null {

		// look up the ID in the options, and return the relevant option.
		const option = options[id];
		if (option) {
			return option;
		}

		// or, log an error if not found, and return null.
		console.error(`ID: ${id} cannot be found in the options data`);
		return null;
	}

	/**
	 * Reads the colour saved against a bespoke paint option, given the option id recorded for a region and the hex saved beside it.
	 * Returns null for a standard option, which has no colour of its own, and also for a bespoke one whose colour is missing or
	 * malformed - logging in that case. A missing colour is deliberately not fatal: the option keeps its description and its price,
	 * and the vehicle stays on the paint it has. Rejecting the load instead would make every configuration saved before the field
	 * existed unloadable, which is worse than a car in the wrong colour at the right price.
	 */
	private static readBespokePaintColour(paintColourId: string, savedHex: string | undefined): NumberArray | null {

		// a standard option identifies its own colour, so there is nothing to read.
		if (!Configuration.isBespokePaintColour(paintColourId)) {
			return null;
		}

		// a bespoke option without a colour is a configuration saved before the field existed.
		if (!savedHex) {
			console.error('No colour saved against the bespoke paint option: ', paintColourId);
			return null;
		}

		// hexToRgba logs and returns null on anything it cannot parse.
		return Utilities.hexToRgba(savedHex);
	}

	static getPaintTypeForId(id: string): PaintTypes {

		// n.b. the bespoke ids belong to the paint type whose panel offers them, so that a bespoke colour opens that panel and is
		// summarised under that heading, exactly as one of its fixed colours would be.
		const solidIds: readonly string[] = [
			'PS006', 'PS004', 'PS001', 'PS002', 'PS003', 'PS005', 'PS014', 'PS015', 'PS013', 'PS012', 'PS017', 'PS018',
			'PS022', 'PS023', 'PS024', BespokePaintColours.Solid
		];

		const metallicIds: readonly string[] = [
			'PM001', 'PM003', 'PM008', 'PM005', 'PM002', 'PM007', 'PM006', 'PM013', 'PM014', 'PM015', 'PM019', 'PM020', 'PM021', 'PM022', 'PS025',
			'PM027', 'PM028', 'PM029', BespokePaintColours.Metallic
		];

		const solitallicIds: readonly string[] = ['PSM001', 'PSM002', 'PSM003', 'PM026'];

		if (solidIds.indexOf(id) !== -1) {
			return PaintTypes.Solid;
		}
		if (metallicIds.indexOf(id) !== -1) {
			return PaintTypes.Metallic;
		}
		if (solitallicIds.indexOf(id) !== -1) {
			return PaintTypes.Solitallic;
		}

		// default to solid if not found.
		return PaintTypes.Solid;
	}

	static getWheelTypeForId(id: string): WheelTypes {

		const alloyIds: readonly string[] = [
			'W005', 'W006'
		];

		const wireIds: readonly string[] = [
			'W007', 'W008', 'W009'
		];

		if (alloyIds.indexOf(id) !== -1) {
			return WheelTypes.Alloy;
		}
		if (wireIds.indexOf(id) !== -1) {
			return WheelTypes.Wire;
		}

		// default to alloy if not found.
		return WheelTypes.Alloy;
	}

	static getDoorGraphicTypeForId(id: string): DoorGraphicTypes {

		const blackIds: readonly string[] = [
			'DK002', 'DK050', 'DK051', 'DK052', 'DK053', 'DK054', 'DK055', 'DK056', 'DK057', 'DK058', 'DK059'
		];

		const clearIds: readonly string[] = [
			'DK003', 'DK060', 'DK061', 'DK062', 'DK063', 'DK064', 'DK065', 'DK066', 'DK067', 'DK068', 'DK069'
		];

		const whiteIds: readonly string[] = [
			'DK001', 'DK040', 'DK041', 'DK042', 'DK043', 'DK044', 'DK045', 'DK046', 'DK047', 'DK048', 'DK049'
		];

		if (blackIds.indexOf(id) !== -1) {
			return DoorGraphicTypes.Black;
		}
		if (clearIds.indexOf(id) !== -1) {
			return DoorGraphicTypes.Clear;
		}
		if (whiteIds.indexOf(id) !== -1) {
			return DoorGraphicTypes.White;
		}

		// default to black if not found.
		return DoorGraphicTypes.Black;
	}

	static getLeatherTypesForId(id: string): { leatherType: LeatherTypes, leatherSubType: LeatherTypes } {

		// group IDs by type (or by type and subtype for two-tone).
		const pebbleGrainIds: readonly string[] = [
			'LP001', 'LP003', 'LP002', 'LP013', 'LP010', 'LP011', 'LP006', 'LP009'
		];
		const softGrainIds: readonly string[] = [
			'LS002', 'LS003', 'LS001', 'LS004', 'LS005', 'LS010', 'LS008', 'LS009', 'LP007', 'LS011'
		];
		const twoTonePebbleGrainIds: readonly string[] = [
			'LP018', 'LP019', 'LP020'
		];
		const twoToneSoftGrainIds: readonly string[] = [
			'LP021', 'LP024', 'LP023'
		];

		// default to two-tone / pebble grain.
		let leatherType = LeatherTypes.TwoTone;
		let leatherSubType = LeatherTypes.PebbleGrain;
		
		if (pebbleGrainIds.indexOf(id) !== -1) {
			leatherType = LeatherTypes.PebbleGrain;
			leatherSubType = LeatherTypes.PebbleGrain;
		}
		else if (softGrainIds.indexOf(id) !== -1) {
			leatherType = LeatherTypes.SoftGrain;
			leatherSubType = LeatherTypes.SoftGrain;
		}
		else if (twoTonePebbleGrainIds.indexOf(id) !== -1) {
			leatherType = LeatherTypes.TwoTone;
			leatherSubType = LeatherTypes.PebbleGrain;
		}
		else if (twoToneSoftGrainIds.indexOf(id) !== -1) {
			leatherType = LeatherTypes.TwoTone;
			leatherSubType = LeatherTypes.SoftGrain;
		}
		return { leatherType, leatherSubType }
	}

	static getSpareWheelId(id: string): string {

		// map the wheel IDs to the spare wheel IDs.
		const spareWheelIds: Record<string, string> = {
			W005: 'W010', // silver alloy.
			W006: 'W011', // black alloy.
			W007: 'W012', // silver painted wire.
			W008: 'W013', // black painted wire.
			W009: 'W014', // polished wire.
			W021: 'W032', // dark grey wire.
			W022: 'W027', // dark red wire.
			W023: 'W028', // dark blue wire.
			W024: 'W029', // dark green wire.
		};

		// return the corresponding ID.
		const spareWheelId = spareWheelIds[id];
		if (spareWheelId) {
			return spareWheelId;
		}
		return 'NONE';
	}

	static getSpareWheelAccessories(): Set<string> {
		return new Set<string>(['COV014', 'W016']);
	}

	static getLuggageRackAccessories(): Set<string> {
		return new Set<string>(['AM031', 'AM033', 'AM029']);
	}
}